import React, { useCallback } from "react";
import { Form, Formik } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import ClientFilter from "../../core/api/clients/ClientFilter";
import ClientBulkUpdateModel from "../../core/api/clients/ClientBulkUpdateModel";
import ManagersSelectInput from "../classifier/ManagersSelectInput";
import ClientStateSelectInput from "../classifier/ClientStateSelectInput";
import { confirmDialog } from "../common/ConfirmDialog";
import { useNotifications } from "../../hooks/NotificationProvider";
import ClientModel from "../../core/api/clients/ClientModel";
import * as Yup from "yup";

const StyledTextField = styled(TextField)({
  "& > *": {
    width: "100%",
  },
});

const StyledGrid = styled(Grid)({
  width: "100%",
});

interface Props {
  filter: ClientFilter;
  close(): void;
  refresh(): void;
}

const initialValues: ClientBulkUpdateModel = {
  clientState: "",
  validFrom: "",
  validTo: "",
  responsibleUser: "",
};

const validationSchema = Yup.object().shape({
  validFrom: Yup.date().nullable(),
  validTo: Yup.date().nullable().min(
    Yup.ref('validFrom'),
    'Galiojimo pabaigos data turi būti vėlesnė už pradžios datą'
  ),
  clientState: Yup.string(),
  responsibleUser: Yup.string(),
});

export default function ClientsBulkUpdateModalForm({ filter, close, refresh }: Props) {
  const notifications = useNotifications();

  const handleSubmit = useCallback(async (values: ClientBulkUpdateModel) => {
    const count = await ClientModel.count(filter);
    
    confirmDialog({
      title: "Atnaujinti klientus?",
      body: `Ar tikrai atnaujinti klientus (${count})?`,
      action: async () => {
        try {
          const number = await ClientBulkUpdateModel.updateList(filter, values);
          notifications.notify({
            level: "success",
            message: `Atnaujintų klientų skaičius: ${number}`,
          });
          close();
          refresh();
        } catch (err: any) {
          notifications.notify({
            level: "error",
            message: err.response?.data?.description || "Įvyko klaida atnaujinant klientus",
          });
        }
      },
      actionName: "Atnaujinti",
    });
  }, [filter, close, refresh, notifications]);

  return (
    <Dialog open onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Daugelio klientų modifikavimo forma</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await handleSubmit(values);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, values, handleChange, handleBlur, errors, touched, setFieldValue }) => (
          <Form>
            <DialogContent>
              <Grid container spacing={2} justifyContent="center">
                <StyledGrid size={10}>
                  <StyledTextField
                    id="validFrom"
                    name="validFrom"
                    type="date"
                    label="Prenumerata galioja nuo"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={values.validFrom}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.validFrom && Boolean(errors.validFrom)}
                    helperText={touched.validFrom && errors.validFrom}
                  />
                </StyledGrid>
                
                <StyledGrid size={10}>
                  <StyledTextField
                    id="validTo"
                    name="validTo"
                    type="date"
                    label="Prenumerata galioja iki"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={values.validTo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.validTo && Boolean(errors.validTo)}
                    helperText={touched.validTo && errors.validTo}
                  />
                </StyledGrid>
                
                <StyledGrid size={10}>
                  <ClientStateSelectInput
                    name="clientState"
                    label="Kliento statusas"
                    fullWidth
                    value={values.clientState || ""}
                    onChange={(e) => setFieldValue('clientState', e.target.value)}
                    onBlur={handleBlur}
                    errors={errors}
                    touched={touched}
                  />
                </StyledGrid>

                <StyledGrid size={10}>
                  <ManagersSelectInput
                    name="responsibleUser"
                    label="Atsakingas asmuo"
                    fullWidth
                    value={values.responsibleUser || ""}
                    onChange={(e) => setFieldValue('responsibleUser', e.target.value)}
                    onBlur={handleBlur}
                  />
                </StyledGrid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                type="button"
                onClick={close}
                disabled={isSubmitting}
              >
                Atšaukti
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                color="primary"
              >
                Išsaugoti
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
